import { Instagram, Facebook, Globe, ArrowUp, Lock, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { api } from "@/lib/api";

const LOGO = "/assets/logo_transparent.png";

export default function Footer() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    api.getSettings().then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const s = settings || { phone: "(55) 4442-1964", email: "raziel.zavala@merzasoluciones.com", address: "Ciudad de México, México", instagram: "https://www.instagram.com/zym_arquitectos", facebook: "https://www.facebook.com/zym_arquitectos", website: "https://www.merzasoluciones.com", whatsapp: "5544421964" };

  return (
    <footer className="bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="footer">
      {/* Decorative grid */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
        backgroundSize: "100px 100px",
      }} />

      <div ref={ref} className="relative z-10 py-20 md:py-28 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          {/* Top — Large brand */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="mb-16 md:mb-24"
          >
            <div className="relative w-fit mb-6">
              {/* Shimmer */}
              <div className="absolute inset-0 z-10 pointer-events-none">
                <div style={{
                  background: "linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.06) 44%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 56%, transparent 62%)",
                  backgroundSize: "250% 100%",
                  animation: "shimmerSweep 5s ease-in-out infinite",
                  position: "absolute", inset: 0,
                }} />
              </div>
              {/* Ambient glow */}
              <motion.div
                animate={{ opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -inset-6 blur-2xl"
                style={{ background: "radial-gradient(ellipse at center, rgba(200,200,220,0.08) 0%, transparent 65%)" }}
              />
                            <img src={LOGO} alt="Merza Soluciones — Z&M Arquitectos" className="h-14 md:h-20 relative" />
            </div>
            <p className="text-neutral-500 text-base md:text-lg font-light leading-relaxed max-w-md">
              Arquitectura & Construcción de alto nivel. Transformamos visiones en espacios extraordinarios.
            </p>
          </motion.div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 mb-16">
            {/* Links */}
            <div className="md:col-span-3">
              <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-600 mb-6">Navegación</p>
              <div className="space-y-3">
                {["Inicio", "Servicios", "Proyectos", "Contacto"].map((link) => (
                  <button
                    key={link}
                    onClick={() => {
                      const id = link === "Inicio" ? "hero" : link.toLowerCase();
                      document.querySelector(`#${id}`)?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="block text-neutral-500 text-sm hover:text-white transition-colors duration-300"
                    data-testid={`footer-link-${link.toLowerCase()}`}
                  >
                    {link}
                  </button>
                ))}
              </div>
            </div>

            {/* Services */}
            <div className="md:col-span-3">
              <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-600 mb-6">Servicios</p>
              <div className="space-y-3">
                {["Diseño Arquitectónico", "Construcción", "Naves Industriales", "Gestoría", "Render 3D", "Remodelación"].map((s) => (
                  <p key={s} className="text-neutral-500 text-sm">{s}</p>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className="md:col-span-3">
              <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-600 mb-6">Contacto</p>
              <div className="space-y-3 text-neutral-500 text-sm">
                <a href={`https://wa.me/${s.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-green-400 transition-colors" data-testid="footer-whatsapp">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  {s.phone}
                </a>
                <p>{s.email}</p>
                <p>{s.address}</p>
              </div>
            </div>

            {/* Social */}
            <div className="md:col-span-3">
              <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-600 mb-6">Redes Sociales</p>
              <div className="flex gap-3">
                {[
                  { icon: Instagram, href: s.instagram, id: "social-instagram" },
                  { icon: Facebook, href: s.facebook, id: "social-facebook" },
                  { icon: Globe, href: s.website, id: "social-web" },
                ].map((item) => (
                  <a
                    key={item.id}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 rounded-full border border-neutral-800 flex items-center justify-center text-neutral-600 hover:text-white hover:border-neutral-500 hover:bg-white/5 transition-all duration-300"
                    data-testid={item.id}
                  >
                    <item.icon size={16} strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom */}
          <div className="border-t border-neutral-800/60 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-neutral-700 text-xs">
              &copy; {new Date().getFullYear()} Merza Soluciones — Z&M+Arquitectos. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin/login")}
                className="flex items-center gap-1.5 text-neutral-700 hover:text-neutral-400 transition-colors text-xs"
                data-testid="admin-access-link"
              >
                <Lock size={11} strokeWidth={1.5} /> Admin
              </button>
              <button
                onClick={scrollTop}
                className="w-10 h-10 rounded-full border border-neutral-800 flex items-center justify-center text-neutral-600 hover:text-white hover:border-neutral-500 transition-all group"
                data-testid="scroll-to-top-button"
              >
                <ArrowUp size={16} strokeWidth={1.5} className="group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
